<template>
  <b-modal
    visible
    hide-header-close
    no-close-on-backdrop
    size="md"
    @hide="$emit('close')"
    body-class="p-4"
    footer-class="d-flex justify-content-end px-4 py-3 border-top"
    header-class="d-flex align-items-center justify-content-between px-4 py-3 border-bottom"
  >
    <!-- Header -->
    <template #modal-header>
      <h6 class="font-weight-bold text-dark mb-0">Export Report</h6>
      <button
        @click="$emit('close')"
        class="btn btn-light btn-sm border-0 d-flex align-items-center justify-content-center p-1"
        style="width:30px;height:30px;border-radius:6px;"
      >
        <i class="fas fa-times text-secondary"></i>
      </button>
    </template>

    <!-- Body -->
    <div class="form-group mb-4">
      <label class="font-weight-bold text-secondary mb-1" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
        Report Type
      </label>
      <select v-model="exportForm.reportType" @change="onReportTypeChange" class="form-control form-control-sm">
        <option value="sales_summary">Sales Summary</option>
        <option value="sales_detail">Sales Detail (Line Items)</option>
        <option value="payment_reconciliation">Payment Reconciliation</option>
        <option value="staff_performance">Staff Performance</option>
        <option value="profit_loss">Profit &amp; Loss</option>
        <option value="sales_tax">Sales Tax</option>
        <option value="general_ledger">General Ledger</option>
      </select>
    </div>

    <div class="form-group mb-4">
      <label class="font-weight-bold text-secondary mb-2 d-block" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
        Format
      </label>
      <div class="row no-gutters" style="gap:0;">
        <div class="col pr-2" v-if="pdfSupported">
          <label
            class="w-100 mb-0 text-center p-3 border d-flex flex-column align-items-center"
            :class="exportForm.format === 'pdf' ? 'bg-dark text-light border-dark' : 'bg-white text-dark'"
            style="border-radius:8px;cursor:pointer;transition:all 0.15s;"
          >
            <input type="radio" v-model="exportForm.format" value="pdf" class="d-none">
            <i class="fas fa-file-pdf mb-2" style="font-size:24px;"></i>
            <span class="font-weight-bold" style="font-size:13px;">PDF</span>
            <small :class="exportForm.format === 'pdf' ? 'text-light' : 'text-secondary'" style="font-size:11px;">Formatted report</small>
          </label>
        </div>
        <div class="col pl-2">
          <label
            class="w-100 mb-0 text-center p-3 border d-flex flex-column align-items-center"
            :class="exportForm.format === 'csv' ? 'bg-dark text-light border-dark' : 'bg-white text-dark'"
            style="border-radius:8px;cursor:pointer;transition:all 0.15s;"
          >
            <input type="radio" v-model="exportForm.format" value="csv" class="d-none">
            <i class="fas fa-file-csv mb-2" style="font-size:24px;"></i>
            <span class="font-weight-bold" style="font-size:13px;">CSV</span>
            <small :class="exportForm.format === 'csv' ? 'text-light' : 'text-secondary'" style="font-size:11px;">Spreadsheet compatible</small>
          </label>
        </div>
      </div>
      <small v-if="!pdfSupported" class="text-secondary d-block mt-2" style="font-size:11px;">
        This report is only available as CSV.
      </small>
    </div>

    <div class="form-group mb-4">
      <label class="font-weight-bold text-secondary mb-1 d-block" style="font-size:12px;letter-spacing:0.4px;text-transform:uppercase;">
        Date Range
      </label>
      <div class="d-flex align-items-center" style="gap:10px;">
        <date-range-picker :auto-apply="true" :ranges="false" :append-to-body="true" v-model="dateRangePicker" class="w-100">
          <template #input="picker">
            <span v-if="picker.startDate && picker.endDate">{{ formatPickerDate(picker.startDate) }} - {{ formatPickerDate(picker.endDate) }}</span>
            <span v-else class="text-muted"><i class="bi bi-calendar-event mr-1"></i>Select date range</span>
          </template>
        </date-range-picker>
      </div>
    </div>

    <div class="form-group mb-3">
      <div class="d-flex align-items-start" style="gap:8px;">
        <input
          type="checkbox"
          v-model="exportForm.async"
          id="async-checkbox"
          class="mt-1"
        >
        <label for="async-checkbox" class="mb-0 text-dark" style="font-size:13px;cursor:pointer;">
          Generate in background
          <small class="d-block text-secondary mt-1" style="font-size:11px;">
            You'll be notified when the report is ready
          </small>
        </label>
      </div>
    </div>

    <!-- Error -->
    <div
      v-if="error"
      class="d-flex align-items-center p-3 bg-light border mt-3"
      style="border-radius:6px;gap:8px;"
    >
      <i class="fas fa-exclamation-circle text-secondary"></i>
      <span class="text-dark" style="font-size:13px;">{{ error }}</span>
    </div>

    <!-- Success -->
    <div
      v-if="success"
      class="d-flex align-items-center p-3 bg-dark text-light mt-3"
      style="border-radius:6px;gap:8px;"
    >
      <i class="fas fa-check-circle"></i>
      <span style="font-size:13px;">{{ success }}</span>
    </div>

    <!-- Footer -->
    <template #modal-footer>
      <button @click="$emit('close')" class="btn btn-light btn-sm border font-weight-600 mr-2">
        Cancel
      </button>
      <button
        @click="generateReport"
        :disabled="loading"
        class="btn btn-dark btn-sm d-inline-flex align-items-center"
        style="gap:6px;"
      >
        <i v-if="loading" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-download"></i>
        {{ loading ? 'Generating...' : 'Generate Report' }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import { downloadFile } from '../../services/pdfDownload';
import axios from 'axios';
import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';

export default {
  name: 'ExportModal',
  components: { DateRangePicker },

  props: {
    filters: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      exportForm: {
        reportType: 'sales_summary',
        format: 'pdf',
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        async: false
      },
      loading: false,
      error: null,
      success: null,
      pdfSupportedTypes: ['sales_summary', 'profit_loss', 'sales_tax', 'general_ledger']
    };
  },

  computed: {
    pdfSupported() {
      return this.pdfSupportedTypes.includes(this.exportForm.reportType);
    },
    dateRangePicker: {
      get() {
        return {
          startDate: this.exportForm.startDate ? new Date(this.exportForm.startDate) : null,
          endDate: this.exportForm.endDate ? new Date(this.exportForm.endDate) : null,
        };
      },
      set(val) {
        this.exportForm.startDate = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : null;
        this.exportForm.endDate = val && val.endDate ? moment(val.endDate).format('YYYY-MM-DD') : null;
      },
    },
  },

  methods: {
    formatPickerDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    onReportTypeChange() {
      if (!this.pdfSupported) {
        this.exportForm.format = 'csv';
      }
    },

    async generateReport() {
      this.loading = true;
      this.error = null;
      this.success = null;

      try {
        const response = await axios.post('/api/sales-analytics/reports/retail/generate', {
          report_type: this.exportForm.reportType,
          format: this.exportForm.format,
          filters: {
            start_date: this.exportForm.startDate,
            end_date: this.exportForm.endDate,
            branch_id: this.filters.branchId,
            cashier_id: this.filters.cashierId
          },
          async: this.exportForm.async
        });

        if (response.data.status === 'queued') {
          this.success = 'Report queued successfully. You will be notified when ready.';
          setTimeout(() => this.$emit('close'), 2000);
        } else if (response.data.report_url || response.data.download_url) {
          this.success = 'Report generated successfully!';
          await downloadFile(response.data.report_url || response.data.download_url, response.data.filename || 'report');
          setTimeout(() => this.$emit('close'), 1500);
        }
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to generate report';
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
